import {ServiceBrandPanel} from '@waka/compat/ServiceBrandPanel';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { renderFrame } from '../utils/render-frame';
import {
  useBackgroundColor,
  useBrandTemplateId,
  useDimensions,
  useFps,
  useTimelineContext,
  useWriteContext,
} from '../utils/use-context';
import { ColorPickerField } from './components/color-picker-field';
import {
  AccordionHeader,
  Chip,
  Label,
  LoadingSpinner,
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
  Skeleton,
  Switch,
  toast,
} from '@crabs-ai/ds-react';
import { useLocale, useTranslations } from 'next-intl';
import { useGetBrandTemplatesApi } from '@/features/brand-template/api/get-brand-templates';
import { resolveBrandTemplateDisplayName } from '@/shared/content-language/system-template-name';
import { DtoBrandTemplateType } from '@/shared/constants';
import { getBrandTemplateApi } from '@/features/brand-template/api/get-brand-template';
import { applyBrandTemplateToState } from '@/features/brand-template/lib/apply-brand-template-to-state';
import { applyBrandTemplateBoundaryVideosToState } from '@/features/brand-template/lib/apply-brand-template-boundary-videos-to-state';
import { getBrandTemplateForEditorSourceComposition } from '@/features/brand-template/lib/source-composition-brand-template';
import { loadFontFromTextItem } from '@/features/remotion-editor/editor/utils/text/load-font-from-text-item';
import { useResetEditor } from '@/features/remotion-editor/editor/utils/use-reset-editor';
import { useMaskState } from '@/features/remotion-editor/editor/lib/use-mask-state';
import { useSafeZoneState } from '@/features/remotion-editor/editor/lib/use-safe-zone-state';
import {
  SafeZonePlatform,
  UndoableState,
} from '@/features/remotion-editor/editor/state/types';
import { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import { VideoItem } from '@/features/remotion-editor/editor/items/video/video-item-type';
import { useIsGsExclusive } from '@/shared/hooks/useTenantUiConfig';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';

/**
 * 대지 비율 변경 시 아이템 위치와 크기를 조정하는 함수
 */
const adjustItemsForNewComposition = (
  undoableState: UndoableState,
  newWidth: number,
  newHeight: number,
): UndoableState => {
  const { compositionWidth: oldWidth, compositionHeight: oldHeight } =
    undoableState;

  // 크기가 같으면 변경 없음
  if (oldWidth === newWidth && oldHeight === newHeight) {
    return undoableState;
  }

  const widthRatio = newWidth / oldWidth;
  const heightRatio = newHeight / oldHeight;

  const adjustedItems: Record<string, EditorStarterItem> = {};

  Object.entries(undoableState.items).forEach(([itemId, item]) => {
    if (item.type === 'video') {
      // 비디오 아이템: 새 대지 크기에 맞게 fit 계산
      const videoItem = item as VideoItem;
      const videoAsset = undoableState.assets[videoItem.assetId];

      if (videoAsset && videoAsset.type === 'video') {
        const assetWidth = videoAsset.width || videoItem.width || newWidth;
        const assetHeight = videoAsset.height || videoItem.height || newHeight;
        const videoAspectRatio = assetWidth / assetHeight;
        const compositionAspectRatio = newWidth / newHeight;

        let fitWidth: number;
        let fitHeight: number;

        if (videoAspectRatio > compositionAspectRatio) {
          // 비디오가 더 가로로 길면, width를 컴포지션에 맞춤
          fitWidth = newWidth;
          fitHeight = newWidth / videoAspectRatio;
        } else {
          // 비디오가 더 세로로 길면, height를 컴포지션에 맞춤
          fitHeight = newHeight;
          fitWidth = newHeight * videoAspectRatio;
        }

        // 중앙 정렬
        const newLeft = (newWidth - fitWidth) / 2;
        const newTop = (newHeight - fitHeight) / 2;

        adjustedItems[itemId] = {
          ...videoItem,
          width: fitWidth,
          height: fitHeight,
          left: newLeft,
          top: newTop,
        };
      } else {
        // 에셋이 없는 경우 비율에 따라 조정
        adjustedItems[itemId] = {
          ...videoItem,
          left: videoItem.left * widthRatio,
          top: videoItem.top * heightRatio,
          width: videoItem.width * widthRatio,
          height: videoItem.height * heightRatio,
        };
      }
    } else if (item.type === 'captions') {
      // 캡션 아이템: 대지 비율에 따라 위치 조정
      const newAspectRatio = newWidth / newHeight;

      // 가로 중앙 정렬
      const newLeft = (newWidth - item.width) / 2;

      let newTop: number;
      if (newAspectRatio < 1) {
        // 9:16 (세로 비율): 화면 높이의 80% 지점에 고정
        newTop = newHeight * 0.8;
      } else {
        // 16:9, 1:1 (가로 또는 정사각형): 하단 고정 마진
        const fixedBottomMargin = newHeight * 0.05;
        newTop = newHeight - item.height - fixedBottomMargin;
      }

      adjustedItems[itemId] = {
        ...item,
        left: newLeft,
        top: newTop,
      };
    } else if (item.type === 'text') {
      // 텍스트 아이템: 상대적 위치 유지
      // 중심점 기준으로 비율 계산 후 재배치
      const centerX = item.left + item.width / 2;
      const centerY = item.top + item.height / 2;

      const xRatio = centerX / oldWidth;
      const yRatio = centerY / oldHeight;

      const newCenterX = xRatio * newWidth;
      const newCenterY = yRatio * newHeight;

      adjustedItems[itemId] = {
        ...item,
        left: newCenterX - item.width / 2,
        top: newCenterY - item.height / 2,
      };
    } else if (
      item.type === 'image' ||
      item.type === 'gif' ||
      item.type === 'webp'
    ) {
      // 이미지/GIF 아이템: 상대적 위치 유지, 크기는 비율에 따라 조정
      const centerX = item.left + item.width / 2;
      const centerY = item.top + item.height / 2;

      const xRatio = centerX / oldWidth;
      const yRatio = centerY / oldHeight;

      const newCenterX = xRatio * newWidth;
      const newCenterY = yRatio * newHeight;

      // 크기는 더 작은 비율을 사용하여 대지 안에 들어오도록 함
      const scaleRatio = Math.min(widthRatio, heightRatio);

      adjustedItems[itemId] = {
        ...item,
        width: item.width * scaleRatio,
        height: item.height * scaleRatio,
        left: newCenterX - (item.width * scaleRatio) / 2,
        top: newCenterY - (item.height * scaleRatio) / 2,
      };
    } else {
      // 기타 아이템: 비율에 따라 위치와 크기 조정
      adjustedItems[itemId] = {
        ...item,
        left: item.left * widthRatio,
        top: item.top * heightRatio,
        width: item.width * widthRatio,
        height: item.height * heightRatio,
      };
    }
  });

  // 마스크 조정: 마스크 비율은 유지하면서 대지에 맞게 위치/크기 조정
  let adjustedMask = undoableState.mask;
  if (undoableState.mask) {
    const mask = undoableState.mask;

    // 마스크의 aspect ratio에 따른 크기 계산
    let maskAspectRatio: number;
    switch (mask.aspectRatio) {
      case '16:9':
        maskAspectRatio = 16 / 9;
        break;
      case '9:16':
        maskAspectRatio = 9 / 16;
        break;
      case '1:1':
        maskAspectRatio = 1;
        break;
      case 'custom':
        // 커스텀의 경우 기존 비율 유지
        maskAspectRatio = mask.width / mask.height;
        break;
      default:
        maskAspectRatio = mask.width / mask.height;
    }

    // 새 대지에 맞게 마스크 크기 계산 (비율 유지)
    const newCompositionAspectRatio = newWidth / newHeight;
    let newMaskWidth: number;
    let newMaskHeight: number;

    if (maskAspectRatio > newCompositionAspectRatio) {
      // 마스크가 더 가로로 길면, width를 대지에 맞춤
      newMaskWidth = newWidth;
      newMaskHeight = newWidth / maskAspectRatio;
    } else {
      // 마스크가 더 세로로 길면, height를 대지에 맞춤
      newMaskHeight = newHeight;
      newMaskWidth = newHeight * maskAspectRatio;
    }

    // 중앙 정렬
    const newMaskX = (newWidth - newMaskWidth) / 2;
    const newMaskY = (newHeight - newMaskHeight) / 2;

    adjustedMask = {
      ...mask,
      x: newMaskX,
      y: newMaskY,
      width: newMaskWidth,
      height: newMaskHeight,
    };
  }

  return {
    ...undoableState,
    compositionWidth: newWidth,
    compositionHeight: newHeight,
    items: adjustedItems,
    mask: adjustedMask,
  };
};

/** hex8(#RRGGBBAA) 문자열에서 base hex와 opacity(0-1)를 분리 */
function parseHexAlpha(color: string): { baseHex: string; opacity: number } {
  if (!color.startsWith('#')) {
    return { baseHex: color, opacity: 1 };
  }
  const clean = color.slice(1);
  if (clean.length === 8) {
    const base = clean.slice(0, 6);
    const alpha = parseInt(clean.slice(6, 8), 16);
    return { baseHex: `#${base}`, opacity: alpha / 255 };
  }
  return { baseHex: color, opacity: 1 };
}

/** base hex + opacity(0-1) → hex8(#RRGGBBAA) 문자열로 병합 */
function mergeHexAlpha(baseColor: string, opacity: number): string {
  if (!baseColor.startsWith('#')) return baseColor;
  const base = baseColor.slice(1, 7);
  if (opacity >= 1) return `#${base}`;
  const alphaHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${base}${alphaHex}`;
}

export type CompositionInspectorProps = {
  /** 프로젝트 길이 변경 옵션 표시 여부 */
  showDuration?: boolean;
  /** 브랜드 템플릿 변경 옵션 표시 여부 */
  showBrandTemplate?: boolean;
  /** 대지 비율 변경 옵션 표시 여부 */
  showLayoutChange?: boolean;
  /** 모든 섹션 닫기 트리거 */
  collapseAllTrigger?: number;
  /** 모든 섹션 열기 트리거 */
  expandAllTrigger?: number;
  /** 섹션 상태 캐시 키 */
  sectionStateKey?: string;
};

export const CompositionInspector = ({
  showDuration = true,
  showBrandTemplate = true,
  showLayoutChange = true,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
  sectionStateKey = 'composition-inspector',
}: CompositionInspectorProps) => {
  const t = useTranslations();
  const { durationInFrames } = useTimelineContext();
  const { fps } = useFps();
  const { compositionWidth, compositionHeight } = useDimensions();
  const { setState } = useWriteContext();
  const { backgroundColor: rawBackgroundColor } = useBackgroundColor();
  const { baseHex: bgBaseHex, opacity: bgOpacity } = useMemo(
    () => parseHexAlpha(rawBackgroundColor),
    [rawBackgroundColor],
  );
  const { mask, setMaskEnabled } = useMaskState();
  const { safeZoneGuide, setSafeZoneEnabled, setSafeZonePlatform } =
    useSafeZoneState();

  // GS 전용 여부 확인 (GS Shop 안전영역 옵션 표시 조건) — space gsExclusive 신 소스
  const isGsrAccountUser = useIsGsExclusive();

  const safeZonePlatformOptions: {
    value: SafeZonePlatform;
    label: string;
  }[] = [
    ...(isGsrAccountUser
      ? [{ value: 'gsshop' as const, label: 'GS Shop' }]
      : []),
    { value: 'youtube', label: 'YouTube' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
  ];

  type RatioOption = {
    label: string;
    width: number;
    height: number;
  };

  const ratioOptions: RatioOption[] = useMemo(
    () => [
      { label: '9:16', width: 1080, height: 1920 },
      { label: '1:1', width: 1920, height: 1920 },
      { label: '16:9', width: 1920, height: 1080 },
    ],
    [],
  );

  const selectedRatio = useMemo(() => {
    const matched = ratioOptions.find(
      (option) =>
        option.width === compositionWidth &&
        option.height === compositionHeight,
    );

    return matched;
  }, [compositionHeight, compositionWidth, ratioOptions]);

  const handleSelectRatio = React.useCallback(
    (option: RatioOption) => {
      setState({
        update: (state) => {
          if (
            state.undoableState.compositionWidth === option.width &&
            state.undoableState.compositionHeight === option.height
          ) {
            return state;
          }

          // 대지 비율 변경 시 아이템 위치와 크기를 조정
          const adjustedUndoableState = adjustItemsForNewComposition(
            state.undoableState,
            option.width,
            option.height,
          );

          return {
            ...state,
            undoableState: adjustedUndoableState,
          };
        },
        commitToUndoStack: true,
      });
    },
    [setState],
  );

  const setBackgroundColor = React.useCallback(
    ({
      color,
      commitToUndoStack,
    }: {
      color: string;
      commitToUndoStack: boolean;
    }) => {
      setState({
        update: (state) => {
          return {
            ...state,
            undoableState: {
              ...state.undoableState,
              backgroundColor: color,
            },
          };
        },
        commitToUndoStack,
      });
    },
    [setState],
  );

  const handleBackgroundColorPreview = useCallback(
    (color: string) => {
      setBackgroundColor({
        color: mergeHexAlpha(color, bgOpacity),
        commitToUndoStack: false,
      });
    },
    [setBackgroundColor, bgOpacity],
  );

  const handleBackgroundColorCommit = useCallback(
    (color: string) => {
      setBackgroundColor({
        color: mergeHexAlpha(color, bgOpacity),
        commitToUndoStack: true,
      });
    },
    [setBackgroundColor, bgOpacity],
  );

  const handleBackgroundOpacityPreview = useCallback(
    (newOpacity: number) => {
      setBackgroundColor({
        color: mergeHexAlpha(bgBaseHex, newOpacity),
        commitToUndoStack: false,
      });
    },
    [setBackgroundColor, bgBaseHex],
  );

  const handleBackgroundOpacityCommit = useCallback(
    (newOpacity: number) => {
      setBackgroundColor({
        color: mergeHexAlpha(bgBaseHex, newOpacity),
        commitToUndoStack: true,
      });
    },
    [setBackgroundColor, bgBaseHex],
  );

  const sectionActive = useMemo(
    () => ({
      layout: true,
      duration: showDuration,
      backgroundColor: true,
      brandTemplate: showBrandTemplate,
      safeZone: selectedRatio?.label === '9:16',
    }),
    [selectedRatio?.label, showBrandTemplate, showDuration],
  );

  const { openSections, toggleSection } = useInspectorSections({
    itemId: sectionStateKey,
    defaults: {
      layout: true,
      duration: true,
      backgroundColor: true,
      brandTemplate: true,
      safeZone: true,
    },
    collapseAllTrigger,
    expandAllTrigger,
    sectionActive,
  });

  return (
    <div className="flex flex-col layout-padding-bottom">
      <div className="flex flex-col gap-2xs">
        <div className="flex flex-col gap-0 border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={
              showLayoutChange
                ? t('Editor.inspector.composition.layout-setting')
                : t('Editor.inspector.composition.layout')
            }
            isOpened={openSections.layout}
            onClick={() => toggleSection('layout')}
          />
          <CollapsibleContent isOpen={openSections.layout}>
            <div className="flex w-full items-center gap-4xs px-md py-4xs">
              {showLayoutChange && (
                <>
                  {ratioOptions.map((option) => (
                    <Chip
                      key={option.label}
                      size="md"
                      labelNode={option.label}
                      selected={selectedRatio?.label === option.label}
                      onClick={() => handleSelectRatio(option)}
                    />
                  ))}
                </>
              )}
              {!showLayoutChange && selectedRatio && (
                <p className="text-comp-moderate-medium text-sys-cont-neutral-default">
                  {selectedRatio.label}{' '}
                  {`(${compositionWidth}x${compositionHeight})`}
                </p>
              )}
            </div>
          </CollapsibleContent>
        </div>
        {showDuration && (
          <div className="flex flex-col gap-0 border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.composition.duration')}
              isOpened={openSections.duration}
              onClick={() => toggleSection('duration')}
            />
            <CollapsibleContent isOpen={openSections.duration}>
              <div className="px-md py-4xs text-comp-moderate-medium">
                {renderFrame(durationInFrames, fps)}
              </div>
            </CollapsibleContent>
          </div>
        )}
        <div className="flex flex-col gap-0 border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.composition.background-color')}
            isOpened={openSections.backgroundColor}
            onClick={() => toggleSection('backgroundColor')}
          />
          <CollapsibleContent isOpen={openSections.backgroundColor}>
            <div className="px-md py-4xs">
              <ColorPickerField
                value={bgBaseHex}
                accessibilityLabel="Background color"
                opacity={bgOpacity}
                onChange={handleBackgroundColorPreview}
                onChangeEnd={handleBackgroundColorCommit}
                onOpacityChange={handleBackgroundOpacityPreview}
                onOpacityChangeEnd={handleBackgroundOpacityCommit}
              />
            </div>
          </CollapsibleContent>
        </div>
        {showBrandTemplate && (
          <div className="flex flex-col gap-0 border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.composition.brand-template')}
              isOpened={openSections.brandTemplate}
              onClick={() => toggleSection('brandTemplate')}
            />
            <CollapsibleContent isOpen={openSections.brandTemplate}>
              <>
                <div className="flex w-full items-center gap-4xs px-md py-2xs">
                  <ServiceBrandPanel />
                </div>
                {mask && (
                  <div className="flex flex-col">
                    <div className="flex w-full items-center justify-between px-md py-2xs">
                      <span className="text-comp-moderate-medium">
                        {t('Editor.inspector.composition.mask-toggle')}
                      </span>
                      <Switch
                        checked={mask.enabled}
                        onCheckedChange={setMaskEnabled}
                      />
                    </div>
                  </div>
                )}
              </>
            </CollapsibleContent>
          </div>
        )}
        {selectedRatio?.label === '9:16' && (
          <div className="flex flex-col gap-0 px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.composition.safe-zone-title')}
              isShowTooltipIcon={true}
              tooltipText={t('Editor.inspector.composition.safe-zone-tooltip')}
              isOpened={openSections.safeZone}
              onClick={() => toggleSection('safeZone')}
            />
            <CollapsibleContent isOpen={openSections.safeZone}>
              <div className="pb-2xs">
                <div className="flex w-full items-center justify-between px-md py-2xs">
                  <span className="text-comp-moderate-medium">
                    {t('Editor.inspector.composition.safe-zone-toggle')}
                  </span>
                  <Switch
                    checked={safeZoneGuide.enabled}
                    onCheckedChange={setSafeZoneEnabled}
                  />
                </div>
                {safeZoneGuide.enabled && (
                  <div className="flex w-full items-center gap-4xs px-md py-2xs">
                    {safeZonePlatformOptions.map((option) => (
                      <Chip
                        className={'min-w-fit'}
                        key={option.value}
                        size="md"
                        labelNode={option.label}
                        selected={safeZoneGuide.platform === option.value}
                        onClick={() => setSafeZonePlatform(option.value)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        )}
      </div>
    </div>
  );
};

function BrandTemplateComponent() {
  const t = useTranslations();
  const locale = useLocale();
  const { data: brandTemplates } = useGetBrandTemplatesApi();
  const { setState } = useWriteContext();
  const { brandTemplateId } = useBrandTemplateId();
  const { durationInFrames } = useTimelineContext();
  const [isApplying, setIsApplying] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { resetEditor } = useResetEditor();
  const { projectType } = useProjectTypeContext();

  // 마지막 요청 ID를 추적하여 race condition 방지
  const latestRequestIdRef = useRef(0);

  // 브랜드 템플릿 변경 및 적용 처리
  const handleChangeBrandTemplateId = useCallback(
    async (id: number, accountBrandTemplateId: number) => {
      const requestId = ++latestRequestIdRef.current;
      try {
        setIsApplying(true);

        // 브랜드 템플릿 데이터 가져오기 (사용자/공통 모두 be-api에서 조회)
        const response = await getBrandTemplateApi(id);

        // race condition 방지: 이후 요청이 이미 시작되었으면 이 응답은 무시
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        const brandTemplate = response.data.payload;

        // 타이틀 폰트 로드 (width 측정 전 로드해야 정확한 값이 나옴)
        const titleStyle = brandTemplate.style.title;
        await loadFontFromTextItem({
          fontFamily: titleStyle.fontFamily,
          fontVariant: titleStyle.fontStyle.variant,
          fontWeight: titleStyle.fontStyle.weight,
          fontInfosDuringRendering: null,
        });

        // race condition 방지: 폰트 로드 사이에 다른 요청이 들어왔으면 무시
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setState({
          update: (state) => {
            const effectiveBrandTemplate =
              getBrandTemplateForEditorSourceComposition({
                brandTemplate,
                projectType,
                compositionWidth: state.undoableState.compositionWidth,
                compositionHeight: state.undoableState.compositionHeight,
              });
            const updatedUndoableState = applyBrandTemplateToState(
              state.undoableState,
              effectiveBrandTemplate,
              durationInFrames,
            );
            const {
              durationInFrames: boundaryDurationInFrames,
              ...updatedUndoableStateWithBoundaryVideos
            } = applyBrandTemplateBoundaryVideosToState(
              updatedUndoableState,
              effectiveBrandTemplate,
            );
            void boundaryDurationInFrames;

            return {
              ...state,
              undoableState: updatedUndoableStateWithBoundaryVideos,
              brandTemplateId: id,
              editorAppliedAccountBrandTemplateId: accountBrandTemplateId,
            };
          },
          commitToUndoStack: true,
        });

        toast({
          description: t('Editor.toast.brand-template-change-success'),
          type: 'success',
          toastOptions: {
            position: 'top-center',
          },
        });
      } catch (error) {
        // race condition으로 인한 무시가 아닌 실제 에러만 처리
        if (requestId === latestRequestIdRef.current) {
          console.error('Failed to apply brand template:', error);
          toast({
            description: t('Editor.toast.brand-template-change-fail'),
            type: 'error',
            toastOptions: {
              position: 'top-center',
            },
          });
        }
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsApplying(false);
        }
      }
    },
    [setState, durationInFrames, t, projectType],
  );

  const selectedBrandTemplateName = useMemo(() => {
    if (!brandTemplateId) {
      return undefined;
    }

    const brandTemplate = brandTemplates?.data.find(
      (template) => template.id === brandTemplateId,
    );

    return resolveBrandTemplateDisplayName(brandTemplate?.name, locale);
  }, [brandTemplateId, brandTemplates?.data, locale]);

  const templateOptions = useMemo(
    () =>
      brandTemplates?.data.map((template) => ({
        id: template.id,
        accountBrandTemplateId: template.accountBrandTemplateId,
        name:
          template.type === DtoBrandTemplateType.default
            ? t('BrandTemplate.default-template')
            : resolveBrandTemplateDisplayName(template.name, locale),
        isInactive: !template.isActive,
        showWakaRecommendLabel: template.type === DtoBrandTemplateType.common,
      })) ?? [],
    [brandTemplates?.data, t, locale],
  );

  if (!brandTemplates?.data) {
    return (
      <div className="flex w-full items-center justify-center">
        <Skeleton className="h-[36px] w-full rounded-2xs" />
      </div>
    );
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
    >
      <PopoverTrigger asChild>
        <SelectFieldTrigger
          value={
            selectedBrandTemplateName ||
            t('Editor.inspector.composition.no-brand-template')
          }
          disabled={isApplying}
          trailingNode={
            isApplying ? <LoadingSpinner size="small" /> : undefined
          }
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-7xl rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)]"
      >
        <div className="flex flex-col">
          {templateOptions.map((option) => {
            const isSelected = brandTemplateId === option.id;
            const isDisabled = option.isInactive || isApplying;

            return (
              <MenuItem
                key={option.id}
                text={option.name}
                size="md"
                singleSelectType
                isSelected={isSelected}
                aria-disabled={isDisabled}
                showLabel={option.showWakaRecommendLabel}
                label={
                  <Label
                    color="primary"
                    size="small"
                  >
                    {t('Common.waka-recommend')}
                  </Label>
                }
                className={`w-full rounded-xs ${
                  isDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-sys-bg-surface-hover'
                }`}
                onClick={() => {
                  if (isDisabled) return;
                  setIsPopoverOpen(false);
                  void handleChangeBrandTemplateId(
                    option.id,
                    option.accountBrandTemplateId,
                  );
                }}
              />
            );
          })}
        </div>
        <div className="flex flex-col py-2xs">
          <button
            type="button"
            className="flex w-full items-center justify-center rounded-3xs p-4xs text-comp-moderate-medium text-sys-cont-neutral-light hover:bg-sys-bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isApplying}
            onClick={async () => {
              setIsPopoverOpen(false);
              setIsApplying(true);
              try {
                await resetEditor({ overrideBrandTemplateId: null });
                toast({
                  description: t('Editor.toast.brand-template-unapply-success'),
                  type: 'success',
                  toastOptions: {
                    position: 'top-center',
                  },
                });
              } finally {
                setIsApplying(false);
              }
            }}
          >
            {t('Editor.inspector.composition.unapply-brand-template')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
